import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { formatCurrency } from "../../utils/helpers";
import { useState } from "react";
import constants from "../../utils/constants";
import { PencilSquareIcon } from "@heroicons/react/24/solid";
import { DateInput, NumberInput, SelectInput, TextInput } from "../Inputs";
import { Save } from "react-bootstrap-icons";
import { PrimaryButton } from "../Buttons";
import { useTenantTheme } from "../../context/useSystemTenantStore";
import OFFER_TEMPLATES, {
  DEFAULT_OFFER_TEMPLATE_ID,
  OFFER_TYPES,
  TYPED_FIELD_DEFAULTS,
  getOfferTemplate,
} from "../../utils/offerTemplates";

const YEAR_IN_DAYS = 365;
export default function TrialOfferDisplay({product, setProduct, qualifyingProductOptions}) {
  const [selectedTrialOffer, setSelectedTrialOffer] = useState();
  const [selectedIndex, setSelectedIndex] = useState();
  // REF: EVO-003 — the template the admin picked for a NEW offer, used to drive the
  // picker UI. SPR-003 T-001: applying a template now also persists `offerType` on
  // the offer (see applyTemplate) so Margot can apply its per-type eligibility gates.
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    DEFAULT_OFFER_TEMPLATE_ID,
  );
  const handleOfferChange = (property, value) => {
    if (!selectedTrialOffer) return;
    setSelectedTrialOffer({
      ...selectedTrialOffer,
      [property]: value,
    });
  };
  // Set multiple offer fields atomically (React batching makes two handleOfferChange
  // calls clobber each other). Used when discount type toggles price/discount fields.
  const applyOfferChanges = (partial) => {
    if (!selectedTrialOffer) return;
    setSelectedTrialOffer({ ...selectedTrialOffer, ...partial });
  };
  // REF: EVO-003 / SPR-003 T-001 — apply a template's preset to the offer being
  // edited. Merges the preset (which now includes `offerType` + reset per-type
  // fields) over the current offer while preserving its identity (trialOfferId /
  // productId). The admin can still edit any field after.
  function applyTemplate(templateId) {
    setSelectedTemplateId(templateId);
    setSelectedTrialOffer((current) =>
      current
        ? { ...current, ...getOfferTemplate(templateId).preset }
        : current,
    );
  }
  function createNewOffer() {
    let newOffer = {
      // REF: DEF-001 — SDK TrialOffer identity is `TrialOfferId`, not `Id`. ASP.NET
      // binds by property name, so emitting `id` left TrialOfferId at 0 and new
      // offers never bound their identity. New offers legitimately use 0 as the
      // "not yet persisted" marker; the key name must match the contract.
      trialOfferId: 0,
      name: "",
      productId: product?.id,
      price: 0,
      durationCount: 7,
      durationType: constants.DURATION_TYPES[0].value,
      daysSinceLastOffer: YEAR_IN_DAYS,
      // REF: DEF-001 — discount is optional. Null (no discount) is a valid pairing;
      // both fields are sent so the payload binds to the SDK contract completely.
      discountType: null,
      discountPercent: null,
      // REF: SPR-003 T-001 — new offers start as Custom (no gating, today's
      // behavior) with the per-type fields null; picking a template sets OfferType.
      offerType: OFFER_TYPES.Custom,
      ...TYPED_FIELD_DEFAULTS,
    };
    let offers = [...product?.trialOffers, newOffer];
    setProduct({...product, trialOffers: offers});
    setSelectedTrialOffer(newOffer);
    setSelectedIndex(offers.length -1);
    // REF: EVO-003 — new offers start on the "Custom" template (today's behavior).
    setSelectedTemplateId(DEFAULT_OFFER_TEMPLATE_ID);
  }
  function saveOffer() {
    let newOffers = [];
    let productOffers = Array.isArray(product?.trialOffers) ? product.trialOffers : [];
    if (Number.isInteger(selectedIndex)) {
      productOffers.splice(selectedIndex, 1, selectedTrialOffer);
      newOffers = productOffers;
    }
    else{                    
      newOffers = [...productOffers, selectedTrialOffer];
    }
    setProduct({...product, trialOffers: newOffers});
    setSelectedTrialOffer(undefined);
    setSelectedIndex(undefined);
  }
  function deselectOffer() {
    setSelectedTrialOffer(undefined);
    setSelectedIndex(undefined);
  }
  function removeOffer(idx) {
    let newOffers = product?.trialOffers?.filter((x, i) => (i !== idx));
    setProduct({...product, trialOffers: newOffers});
  }
  return (
    <div>
      <div className="flex flex-row justify-between mb-6">
        <div className="text-xl font-semibold">Trial Offers</div>
        <div className="w-32">
          <PrimaryButton text={"Add Offer"} onClick={createNewOffer} />
        </div>
      </div>
      {/* REF: EVO-003 Phase 1 — template picker shown only while creating a NEW
          offer (trialOfferId 0). Existing offers keep their saved values. */}
      {!!selectedTrialOffer && selectedTrialOffer.trialOfferId === 0 && (
        <TemplatePicker
          templateId={selectedTemplateId}
          applyTemplate={applyTemplate}
        />
      )}
      <div className={!!selectedTrialOffer ? "mb-14" : ""}>
        <Headers offerSelected={!!selectedTrialOffer}/>
        {product?.trialOffers && product.trialOffers.length && product.trialOffers.map((offer, idx) => {
          return idx === selectedIndex && !!selectedTrialOffer
          ? (<EditOffer offer={selectedTrialOffer} idx={idx} handleOfferChange={handleOfferChange} applyOfferChanges={applyOfferChanges} saveOffer={saveOffer} deselectOffer={deselectOffer} qualifyingProductOptions={qualifyingProductOptions} />)
          : (<ReadOnlyOffer offer={offer} idx={idx} setSelectedTrialOffer={setSelectedTrialOffer} setSelectedIndex={setSelectedIndex} removeOffer={removeOffer}  offerSelected={!!selectedTrialOffer}/>);
        })}
      </div>
    </div>
  );
}

function EditOffer({offer, idx, handleOfferChange, applyOfferChanges, saveOffer, deselectOffer, qualifyingProductOptions}) {
  const theme = useTenantTheme();
  // REF: SPR-004 T-001 — Margot ignores durationCount/durationType in
  // RecurringPerPeriod mode (HANDOFF-004 §2); the fields stay editable (values
  // aren't cleared, since Margot just ignores them) but are de-emphasized below
  // so the admin isn't misled into thinking they still apply.
  const isRecurringMode = offer.discountMode === "RecurringPerPeriod";
  return (
    <div
      key={`product-offer-${idx}`}
      className="px-2 py-4 flex flex-col hover:text-slate-500 hover:bg-gray-200 gap-y-2 rounded-md"
    >
    <div className="flex flex-row gap-x-4">
      <div className="flex flex-col flex-1">
        <TextInput
          // label={"Name"}
          value={offer.name}
          onChange={(e) => handleOfferChange("name", e.target.value)}
          required
          maxLength={250}
        />
      </div>
      <div className="flex flex-col flex-1">
        {/* Price and discount are mutually exclusive: the flat Price shows only when
            there is no discount type. Slot kept so the column grid stays aligned. */}
        {!offer.discountType && (
          <NumberInput
            // label="Price in pennies"
            value={offer.price ?? ""}
            onChange={(e) => handleOfferChange("price", e.target.valueAsNumber)}
            required
            isCurrency
            isPennies
          />
        )}
      </div>
      <div className="flex flex-col flex-1">
        {/* DEF-001: Discount type (optional). Values align to SDK TypeOfDiscount.
            Selecting a type switches the offer to discount-based (zero + hide the flat
            price); clearing it drops the discount amount and restores the Price field. */}
        <SelectInput
          // label="Discount Type"
          options={constants.DISCOUNT_TYPES}
          value={constants.DISCOUNT_TYPES.find(x => x.value === offer.discountType) ?? null}
          onChange={(e) =>
            e?.value
              ? applyOfferChanges({ discountType: e.value, price: 0 })
              : applyOfferChanges({ discountType: null, discountPercent: null })
          }
          onClear={() => applyOfferChanges({ discountType: null, discountPercent: null })}
        />
      </div>
      <div className="flex flex-col flex-1">
        {/* DEF-001: Discount amount — shown only when a discount type is set. Formatted
            per type: currency (pennies) for Fixed, a 0-100 percent for Percentage. */}
        {offer.discountType && (
          <NumberInput
            // label="Discount"
            value={offer.discountPercent ?? ""}
            onChange={(e) => handleOfferChange("discountPercent", Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber)}
            min={0}
            max={offer.discountType === "Percentage" ? 100 : undefined}
            isCurrency={offer.discountType === "Fixed"}
            isPennies={offer.discountType === "Fixed"}
          />
        )}
      </div>
      <div className={`flex flex-col flex-1 ${isRecurringMode ? "opacity-50" : ""}`}>
        <NumberInput
          // label="Duration Amount"
          value={offer.durationCount ?? ""}
          onChange={(e) => handleOfferChange("durationCount", e.target.valueAsNumber)}
          min={0}
          required
          // REF: SPR-004 T-001 — Margot ignores this field in RecurringPerPeriod
          // mode; label it as such rather than misleadingly imply it still applies.
          subLabel={isRecurringMode ? "Not used in recurring mode" : undefined}
        />
      </div>
      <div className={`flex flex-col flex-1 ${isRecurringMode ? "opacity-50" : ""}`}>
        <SelectInput
          // label="Duration Type"
          options={constants.DURATION_TYPES}
          value={constants.DURATION_TYPES.find(x => x.value === offer.durationType)}
          onChange={(e) => handleOfferChange("durationType", e.value)}
          required
        />
        {isRecurringMode && (
          <div className="mt-1 text-gray-400 text-xs text-right">Not used in recurring mode</div>
        )}
      </div>
      <div className="flex flex-col flex-1">
        <NumberInput
          // label={"Days Since Last Offer Threshold"}
          value={offer.daysSinceLastOffer ?? ""}
          onChange={(e) => handleOfferChange("daysSinceLastOffer", e.target.valueAsNumber)}
          min={0}
          required
        />
      </div>
      <div className="flex flex-row gap-x-4 w-20">
        <CheckIcon
          title="Save"
          className="h-6 w-6 text-white bg-primaryColor rounded-md p-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            saveOffer();
          }}
          style={{ backgroundColor: theme?.primaryHexColor }}
        />
        <XMarkIcon
          title="Cancel"
          className="h-6 w-6 text-gray-700 bg-gray-300 rounded-md p-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            deselectOffer();
          }}
        />
      </div>
    </div>
    {/* REF: SPR-004 T-001 — DiscountMode + conditional RecurringPeriods, shown
        only for discount-bearing offers. */}
    <DiscountModeFields offer={offer} handleOfferChange={handleOfferChange} applyOfferChanges={applyOfferChanges} />
    {/* REF: SPR-003 T-001 — per-type inputs surfaced conditionally by offerType. */}
    <TypedOfferFields offer={offer} handleOfferChange={handleOfferChange} qualifyingProductOptions={qualifyingProductOptions} />
    </div>
  );
}

// REF: SPR-004 T-001 — application-mode control for discount-bearing offers
// (HANDOFF-004 §2). Only shown when a discount type is set (offer.discountType
// truthy) — a plain-price offer has no discount to apply a mode to. Defaults to
// SingleTerm (today's only behavior) when unset. Switching to RecurringPerPeriod
// keeps durationCount/durationType untouched (Margot just ignores them in that
// mode — see the de-emphasized fields above); switching back to SingleTerm nulls
// recurringPeriods atomically (applyOfferChanges) so a stale N never round-trips
// against a SingleTerm offer, mirroring the discount-type toggle's pattern above.
function DiscountModeFields({ offer, handleOfferChange, applyOfferChanges }) {
  if (!offer?.discountType) return null;
  const mode = offer.discountMode || constants.DISCOUNT_MODES[0].value;
  const modeOption = constants.DISCOUNT_MODES.find((x) => x.value === mode);
  const isRecurring = mode === "RecurringPerPeriod";

  return (
    <div className="flex flex-row flex-wrap gap-4 px-2 pb-1">
      <div className="flex flex-col w-64">
        <SelectInput
          label="Discount Mode"
          options={constants.DISCOUNT_MODES}
          value={modeOption ?? null}
          onChange={(e) =>
            e?.value === "RecurringPerPeriod"
              ? applyOfferChanges({ discountMode: e.value })
              : applyOfferChanges({
                  discountMode: constants.DISCOUNT_MODES[0].value,
                  recurringPeriods: null,
                })
          }
          required
        />
        {/* HANDOFF-004 §2 "Good for" copy, so admins pick the right mode. */}
        {modeOption && (
          <div className="mt-1 text-gray-400 text-xs">{modeOption.description}</div>
        )}
      </div>
      {isRecurring && (
        <div className="flex flex-col w-56">
          <NumberInput
            label="Recurring periods"
            subLabel="Required — at least 2 billing periods"
            value={offer.recurringPeriods ?? ""}
            onChange={(e) =>
              handleOfferChange(
                "recurringPeriods",
                Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber,
              )
            }
            min={2}
            required
          />
        </div>
      )}
    </div>
  );
}

// REF: SPR-003 T-001 — the per-type fields for a typed offer. Only the input(s)
// relevant to the offer's OfferType are shown (Custom shows nothing — behaves
// exactly as before). CrossSell → qualifyingProductId, SeatExpansion →
// minSeatQuantity, Loyalty → minTenureDays, Seasonal → campaign window. These map
// 1:1 to the SDK TrialOffer fields Margot uses for eligibility gating.
function TypedOfferFields({ offer, handleOfferChange, qualifyingProductOptions }) {
  const offerType = offer?.offerType;
  if (!offerType || offerType === OFFER_TYPES.Custom) return null;

  const numberOrNull = (e) =>
    Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;

  return (
    <div className="flex flex-row flex-wrap gap-4 px-2 pb-1">
      {offerType === OFFER_TYPES.CrossSell && (
        <div className="flex flex-col w-56">
          {/* Dropdown of the tenant's other products; stores the selected productId. */}
          <SelectInput
            label="Qualifying product"
            subLabel="Required — customer must own this product"
            options={qualifyingProductOptions ?? []}
            value={(qualifyingProductOptions ?? []).find((o) => o.value === offer.qualifyingProductId) ?? null}
            onChange={(e) => handleOfferChange("qualifyingProductId", e?.value ?? null)}
            required
          />
        </div>
      )}
      {offerType === OFFER_TYPES.SeatExpansion && (
        <div className="flex flex-col w-56">
          <NumberInput
            label="Minimum seat quantity"
            subLabel="Required — seats must meet this threshold"
            value={offer.minSeatQuantity ?? ""}
            onChange={(e) =>
              handleOfferChange("minSeatQuantity", numberOrNull(e))
            }
            min={0}
            required
          />
        </div>
      )}
      {offerType === OFFER_TYPES.Loyalty && (
        <div className="flex flex-col w-56">
          <NumberInput
            label="Minimum tenure (days)"
            value={offer.minTenureDays ?? ""}
            onChange={(e) =>
              handleOfferChange("minTenureDays", numberOrNull(e))
            }
            min={0}
          />
        </div>
      )}
      {offerType === OFFER_TYPES.Seasonal && (
        <>
          <div className="flex flex-col w-56">
            <DateInput
              label="Campaign starts"
              value={offer.campaignStartsAt ?? ""}
              onChange={(e) =>
                handleOfferChange("campaignStartsAt", e.target.value || null)
              }
            />
          </div>
          <div className="flex flex-col w-56">
            <DateInput
              label="Campaign ends"
              value={offer.campaignEndsAt ?? ""}
              onChange={(e) =>
                handleOfferChange("campaignEndsAt", e.target.value || null)
              }
            />
          </div>
        </>
      )}
    </div>
  );
}

function ReadOnlyOffer({offer, idx, setSelectedTrialOffer, setSelectedIndex, removeOffer, offerSelected}) {
  const theme = useTenantTheme();
  const duration = constants.DURATION_TYPES.find(x => x.value === offer?.durationType);
  return (
    <div 
      key={`product-offer-${idx}`} 
      className="px-2 py-4 flex flex-row hover:text-slate-500 hover:bg-gray-200 gap-x-4 rounded-md"
    >
      <div className="flex flex-1">{offer.name}</div>
      <div className="flex flex-1">{formatCurrency(offer.price, true)}</div>
      {/* DEF-001: surface discount so it round-trips visibly after save/reload. */}
      <div className="flex flex-1">{offer.discountType || "—"}</div>
      <div className="flex flex-1">{offer.discountPercent ?? "—"}</div>
      {offerSelected
      ? (
        <>
          <div className="flex flex-1">{offer.durationCount}</div>
          <div className="flex flex-1">{offer.durationCount === 1 ? duration?.singular : duration?.label}</div>
        </>
      )
      : (<div className="flex flex-1">{offer.durationCount} {offer.durationCount === 1 ? duration?.singular : duration?.label}</div>)}
      <div className="flex flex-1">{offer.daysSinceLastOffer} {offer.durationCount === 1 ? "Day" : "Days"}</div>
      <div className="flex flex-row gap-x-4 w-20">
        <PencilSquareIcon
          title="Edit"
          className="h-6 w-6 text-white bg-primaryColor rounded-md p-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTrialOffer(offer);
            setSelectedIndex(idx);
          }}
          style={{ backgroundColor: theme?.primaryHexColor }}
        />
        <XMarkIcon
          title="Remove"
          className="h-6 w-6 text-white bg-delete rounded-md p-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            removeOffer(idx);
          }}
        />
      </div>
    </div>
  );
}

// REF: EVO-003 Phase 1 — a UI-time convenience that pre-fills the offer form from
// a named template and explains, in plain English, what the offer is for and
// which gating the Margot subscription service enforces. The template is NOT
// persisted; only the pre-filled offer fields are saved.
function TemplatePicker({ templateId, applyTemplate }) {
  const options = OFFER_TEMPLATES.map((t) => ({ label: t.label, value: t.id }));
  const template = getOfferTemplate(templateId);
  return (
    <div className="mb-6 p-4 bg-gray-100 rounded-md flex flex-col gap-y-2">
      <div className="text-sm font-semibold">Start from a template</div>
      <div className="max-w-md">
        <SelectInput
          label="Offer template"
          options={options}
          value={options.find((o) => o.value === templateId) ?? null}
          onChange={(e) => applyTemplate(e.value)}
          isSearchable
        />
      </div>
      {template && (
        <div className="text-sm text-gray-600">{template.description}</div>
      )}
      {template && (
        <div className="text-xs text-gray-500">
          <span className="font-semibold">Behavior: </span>
          {template.margotBehaviorNote}
        </div>
      )}
      <div className="text-xs italic text-gray-400">
        Templates pre-fill the fields below; you can still edit any value. The
        template itself is not saved — behavioral eligibility/gating is applied by
        the subscription service, not here.
      </div>
    </div>
  );
}

function Headers({offerSelected}) {
  const theme = useTenantTheme();
  return (
    <div 
      key={`headers`} 
      className="px-2 py-4 flex flex-row text-white bg-primaryColor gap-x-4 rounded-sm"
      style={{ backgroundColor: theme?.primaryHexColor }}
    >
      <div className="flex flex-1">Name</div>
      <div className="flex flex-1">Price</div>
      <div className="flex flex-1">Discount type</div>
      <div className="flex flex-1">Discount</div>
      {offerSelected
      ? (
        <>
          <div className="flex flex-1">Duration amount</div>
          <div className="flex flex-1">Duration type</div>
        </>
      )
      : (<div className="flex flex-1">Duration</div>)}
      <div className="flex flex-1">Days between offer eligibility</div>
      <div className="flex w-20">
      </div>
    </div>
  );
}